import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

const IMPORTANT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#d32f2f',
      borderWidth: '1.5px',
    },
    '&:hover fieldset': {
      borderColor: '#c62828',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#d32f2f',
      borderWidth: '2px',
    },
  },
};

const IMPORTANT_LABELS = new Set(['First Name', 'Last Name', 'First Name', 'AMKA', 'Age', 'Diagnosis', 'Card Date', 'Phone']);

const DEFAULT_MULTILINE_KEYS = new Set(['Address', 'Notes', '__address', '__notes']);

const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }

  return String(value);
};

const getDisplayFields = (patient) => {
  if (!patient) {
    return [];
  }

  if (Array.isArray(patient.__detailEntries) && patient.__detailEntries.length) {
    return patient.__detailEntries.map((entry) => ({
      label: entry.label,
      value: entry.value,
      multiline: Boolean(entry.multiline) || DEFAULT_MULTILINE_KEYS.has(entry.label),
    }));
  }

  const fallbackFields = [
    { label: 'First Name', value: patient.first_name ?? patient['ΟΝΟΜΑ'] },
    { label: 'Last Name', value: patient.last_name ?? patient['ΕΠΙΘΕΤΟ'] },
    { label: 'Date of Birth', value: patient.__dateOfBirth ?? patient.date_of_birth },
    { label: 'Age', value: patient.__age },
    { label: 'Gender', value: patient.__gender ?? patient.gender },
    { label: 'Phone', value: patient.__phone ?? patient.phone },
    { label: 'Email', value: patient.__email ?? patient.email },
    { label: 'Address', value: patient.__address ?? patient.address, multiline: true },
    { label: 'Notes', value: patient.__notes ?? patient.notes, multiline: true },
    { label: 'Last Visit', value: patient.__lastVisit ?? patient.last_visit },
  ];

  return fallbackFields
    .map((field) => ({
      label: field.label,
      value: formatValue(field.value),
      multiline: Boolean(field.multiline) || DEFAULT_MULTILINE_KEYS.has(field.label),
    }))
    .filter((field) => field.value !== '—');
};

const PatientDetailDialog = ({ open, patient, onClose }) => {
  const fields = getDisplayFields(patient);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Patient Details</DialogTitle>
      <DialogContent dividers>
        {patient ? (
          <Grid container spacing={2} mt={0.5}>
            {fields.map((field) => (
              <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.label}>
                <TextField
                  label={field.label}
                  value={field.value ?? '—'}
                  fullWidth
                  multiline={field.multiline}
                  minRows={field.multiline ? 3 : 1}
                  InputProps={{ readOnly: true }}
                  sx={IMPORTANT_LABELS.has(field.label) ? IMPORTANT_FIELD_SX : {}}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                ID: {patient.__recordId ?? patient.id ?? '—'}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Select a patient to view their information.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDetailDialog;
